import React, { useState, useEffect } from 'react';
import { X, CheckCircle, XCircle, FileText } from 'lucide-react';
import { renderLatex } from '../../lib/latex';
import { getImageUrl, getImageUrlSync } from '../../utils/imageUtils';

const ViewQuestionModal = ({ isOpen, onClose, question }) => {
  const [questionImageUrl, setQuestionImageUrl] = useState(null);
  const [explanationImageUrl, setExplanationImageUrl] = useState(null);
  const [optionImageUrls, setOptionImageUrls] = useState({});

  // Load image URLs when question changes
  useEffect(() => {
    if (!question) return;

    // Load question image
    if (question.questionImageUrl) {
      setQuestionImageUrl(getImageUrlSync(question.questionImageUrl));
      getImageUrl(question.questionImageUrl).then(url => {
        if (url) setQuestionImageUrl(url);
      }).catch(console.warn);
    } else {
      setQuestionImageUrl(null);
    }

    // Load explanation image
    if (question.explanationImageUrl) {
      setExplanationImageUrl(getImageUrlSync(question.explanationImageUrl));
      getImageUrl(question.explanationImageUrl).then(url => {
        if (url) setExplanationImageUrl(url);
      }).catch(console.warn);
    } else {
      setExplanationImageUrl(null);
    }

    // Load option images
    const newOptionImageUrls = {};
    question.options?.forEach((option, index) => {
      if (option.imageUrl) {
        newOptionImageUrls[index] = getImageUrlSync(option.imageUrl);
        getImageUrl(option.imageUrl).then(url => {
          if (url) {
            setOptionImageUrls(prev => ({
              ...prev,
              [index]: url
            }));
          }
        }).catch(console.warn);
      }
    });
    setOptionImageUrls(newOptionImageUrls);
  }, [question]);

  if (!isOpen || !question) return null;

  const normalizedOptions = Array.isArray(question.options)
    ? question.options.map((option) => (typeof option === 'string' ? { text: option } : (option || {})))
    : [];

  const questionTypeValue = (question.questionType || question.type || '').toString();
  const normalizedQuestionType = questionTypeValue.toUpperCase();
  const isNumericFill = normalizedQuestionType === 'FILL_IN_THE_BLANK_NUMERIC';
  const normalizedCorrectAnswer = (question.correctAnswer ?? '').toString().trim();

  const isCorrectOption = (option, index) => {
    if (option?.isCorrect || option?.correct) return true;
    if (!normalizedCorrectAnswer) return false;

    // Supports index-based answers: "1", "2", ...
    if (normalizedCorrectAnswer === (index + 1).toString()) return true;

    // Supports letter-based answers: "A", "B", ...
    if (normalizedCorrectAnswer.toUpperCase() === String.fromCharCode(65 + index)) return true;

    // Supports exact text-based answers.
    if ((option?.text || '').toString().trim().toLowerCase() === normalizedCorrectAnswer.toLowerCase()) return true;

    return false;
  };

  const getDifficultyBadge = (difficulty) => {
    const badges = {
      EASY: 'bg-green-100 text-green-800',
      MEDIUM: 'bg-yellow-100 text-yellow-800',
      HARD: 'bg-red-100 text-red-800'
    };
    const normalizedDifficulty = difficulty?.toUpperCase();
    return `px-2 py-1 text-xs font-medium rounded-full ${badges[normalizedDifficulty] || badges.MEDIUM}`;
  };

  const accessScope = question.accessScope || ((question.tenantId || (question.tenantCodes && question.tenantCodes.length > 0)) ? 'TENANT' : 'GLOBAL');
  const sourceType = question.sourceType || (accessScope === 'GLOBAL' ? 'SUPER_ADMIN' : 'ADMIN');
  const audience = accessScope === 'GLOBAL'
    ? 'All admin tenants'
    : (question.tenantCodes && question.tenantCodes.length > 0
      ? question.tenantCodes.join(', ')
      : (question.tenantId || 'N/A'));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white modal-responsive max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-200">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            <h2 className="text-lg sm:text-xl font-semibold azure-text">View Question</h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors duration-200 p-1"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 form-responsive">
          {/* Passage (for Reading Comprehension) */}
          {question.passage && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Passage
              </label>
              <div className="p-3 sm:p-4 bg-amber-50 rounded-lg border border-amber-200 max-h-64 overflow-y-auto">
                <div className="text-slate-900 whitespace-pre-wrap text-sm sm:text-base">{renderLatex(question.passage)}</div>
              </div>
              {question.passageId && (
                <p className="text-xs text-slate-500 mt-1">Passage ID: {question.passageId}</p>
              )}
            </div>
          )}

          {/* Question Text */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Question Text
            </label>
            <div className="p-3 sm:p-4 bg-slate-50 rounded-lg border">
              <div className="text-slate-900 whitespace-pre-wrap text-sm sm:text-base">{renderLatex(question.questionText || question.text)}</div>
            </div>
          </div>

          {/* Question Image */}
          {question.questionImageUrl && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Question Image
              </label>
              <div className="p-3 sm:p-4 bg-slate-50 rounded-lg border">
                <img
                  src={questionImageUrl}
                  alt="Question"
                  className="max-w-full h-auto rounded-lg shadow-sm"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
          )}

          {/* Correct Answer for Numeric Fill in the Blank */}
          {isNumericFill && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                Correct Answer
              </label>
              <div className="p-4 rounded-lg border-2 border-green-200 bg-green-50 flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                <span className="text-slate-900 font-medium text-lg">{question.correctAnswer || 'N/A'}</span>
              </div>
            </div>
          )}

          {/* Answer Options for MCQ / TRUE_FALSE / other option-based types */}
          {!isNumericFill && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                Answer Options
              </label>
              <div className="mb-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
                <span className="text-sm font-medium text-slate-700">Correct Answer:</span>{' '}
                <span className="text-sm text-slate-900">{question.correctAnswer || 'N/A'}</span>
              </div>
              <div className="space-y-3">
                {normalizedOptions.length === 0 && (
                  <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-800">
                    No answer options available for this question.
                  </div>
                )}
                {normalizedOptions.map((option, index) => {
                  const isCorrect = isCorrectOption(option, index);

                  return (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border-2 flex items-start space-x-3 ${
                        isCorrect
                          ? 'border-green-200 bg-green-50'
                          : 'border-slate-200 bg-white'
                      }`}
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {isCorrect ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : (
                          <XCircle className="h-5 w-5 text-slate-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <span className="font-medium text-slate-600 text-sm">
                          Option {index + 1}:
                        </span>
                        <div className="text-slate-900 mt-1">{renderLatex(option.text)}</div>
                        {option.imageUrl && (
                          <div className="mt-2">
                            <img
                              src={optionImageUrls[index]}
                              alt={`Option ${index + 1}`}
                              className="max-w-full h-auto rounded-lg shadow-sm max-h-48"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                      </div>
                      {isCorrect && (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          Correct
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Question Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Category
                </label>
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <span className="text-slate-900">{question.subject || question.category || 'N/A'}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Difficulty
                </label>
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <span className={getDifficultyBadge(question.difficultyLevel || question.difficulty)}>
                    {(question.difficultyLevel || question.difficulty || 'MEDIUM').toUpperCase()}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Question Type
                </label>
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <span className="text-slate-900">
                    {questionTypeValue ? questionTypeValue.toString().replace(/_/g, ' ') : 'N/A'}
                  </span>
                </div>
              </div>

              {question.classLevel && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Class
                  </label>
                  <div className="p-3 bg-slate-50 rounded-lg border">
                    <span className="text-slate-900">Class {question.classLevel}</span>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Access Scope
                </label>
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${accessScope === 'GLOBAL' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}`}>
                      {accessScope === 'GLOBAL' ? 'Global' : 'Tenant'}
                    </span>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-700">
                      {sourceType === 'SUPER_ADMIN' ? 'Super Admin' : 'Admin'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Accessible to: {audience}</p>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Kit ID
                </label>
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <span className="text-slate-900">{question.kitId || 'N/A'}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Created Date
                </label>
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <span className="text-slate-900">
                    {question.createdAt
                      ? new Date(question.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : 'N/A'
                    }
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Question ID
                </label>
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <span className="text-slate-900 font-mono text-sm">{question.id || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Exam Types */}
          {question.examTypes && question.examTypes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Exam Types
              </label>
              <div className="flex flex-wrap gap-2">
                {question.examTypes.map((type, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full"
                  >
                    {type}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {question.tags && question.tags.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {question.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 text-sm bg-slate-100 text-slate-700 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Reference */}
          {question.reference && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Reference
              </label>
              <div className="p-4 bg-slate-50 rounded-lg border">
                <div className="text-slate-900 whitespace-pre-wrap">{question.reference}</div>
              </div>
            </div>
          )}

          {/* Explanation */}
          {question.explanation && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Explanation
              </label>
              <div className="p-4 bg-slate-50 rounded-lg border">
                <div className="text-slate-900 whitespace-pre-wrap">{renderLatex(question.explanation)}</div>
              </div>
            </div>
          )}

          {/* Explanation Image */}
          {question.explanationImageUrl && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Explanation Image
              </label>
              <div className="p-3 sm:p-4 bg-slate-50 rounded-lg border">
                <img
                  src={explanationImageUrl}
                  alt="Explanation"
                  className="max-w-full h-auto rounded-lg shadow-sm"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 sm:p-6 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="btn-secondary w-full sm:w-auto"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewQuestionModal;
